import {Graphics, Rectangle} from "pixi.js";
import {DrawFrmThing} from "../../drawfrm/DrawFrmThing";
import {ElevationApp} from "../../ElevationApp";
import {MdHardwarePlacement} from "../../model/hardware/MdHardware";
import {HardwareItem} from "../../../../../../api/Hardware";
import {HardwareGlyph, hardwareGlyphFor} from "../../shared-2d/hardwareGlyphs";
import {EdgeHead, glyphStyle} from "../../shared-2d/hardwareGlyphs/HardwareGlyph";
import {doorPlacementViolations, itemFace, ruleForItem} from "../../model/hardware/MdHardwareRules";
import {inches} from "../../const";
import {DoorFace} from "../frameProfile/Helpers";

// a cylindrical latch / deadbolt faceplate mortised into the latch edge, centred on the
// bore height, with its bolt showing through (scene units = mm).
const FACEPLATE_WIDTH = 1 * inches;
const FACEPLATE_HEIGHT = 2.25 * inches;
const FACEPLATE_RADIUS = 0.125 * inches;
const BOLT_WIDTH = 0.5 * inches;
const LATCH_BOLT_HEIGHT = 0.625 * inches;
const DEADBOLT_HEIGHT = 1 * inches;
const OUTLINE = 0.2 * inches;

// the tick marking an item without an edge drawing: a bar across the edge at its height.
const TICK_RISE = 0.75 * inches;
const TICK_STROKE = 1.5;

// padding around a drawing for its click hit area.
const HIT_PAD = 1 * inches;

// the latch edge the item sits on, in the Thing's local coords: `x0..x1` spans the door
// thickness, with the front face at x0 and the back face at x1 (the Locations view folds
// the door out front | edge | back). `head` is the frame head drawn over the edge, when
// the view has one, for hardware that seats on the frame face.
export type EdgeRect = {x0: number; x1: number; head?: EdgeHead};

// One placed hardware item as seen from the leaf's latch edge: a door placement on this
// leaf, or a frame placement on the head over it. An item whose category glyph has an
// edge drawing stands off the face it mounts on (a rim exit device on the push face, at
// its 161 height); otherwise the primary handle and lock draw as the faceplate cut into
// the edge, and any other door placement is a tick across the edge at its height (a head
// placement without an edge drawing is not built, see LatchEdgeView). Click to select the placement (the same selection as its face
// crosshair); the position is edited on the faces, not here.
export class EdgeHardwareThing extends DrawFrmThing {
    private elev: ElevationApp;
    private item: MdHardwarePlacement;
    private product: HardwareItem;
    private categoryGlyph: HardwareGlyph | null;
    private edge: () => EdgeRect;
    private frontFace: () => DoorFace | null;
    private onSelect: (additive: boolean) => void;

    private selectedFlag = false;
    private glyph: Graphics;

    constructor(props: {
        elev: ElevationApp;
        item: MdHardwarePlacement;
        product: HardwareItem;
        edge: () => EdgeRect;
        // the push / pull face at the edge's front side (x0); null when the leaf has no
        // single swing, which stands every item off the front.
        frontFace: () => DoorFace | null;
        onSelect: (additive: boolean) => void;
    }) {
        super();
        this.elev = props.elev;
        this.item = props.item;
        this.product = props.product;
        this.categoryGlyph = hardwareGlyphFor(props.product);
        this.edge = props.edge;
        this.frontFace = props.frontFace;
        this.onSelect = props.onSelect;

        this.glyph = new Graphics();
        this.lines.addChild(this.glyph);

        this.makeInteractive({
            target: this.glyph,
            onClick: (e) => this.onSelect(e.ctrlKey || e.metaKey),
        });

        this.selectedFlag = this.elev.viewSettings.isSelected(this.item.id);
        this.trackSubscription(this.elev.viewSettings.selection.subscribe(() => {
            const next = this.elev.viewSettings.isSelected(this.item.id);
            if (next === this.selectedFlag) return;
            this.selectedFlag = next;
            if (!this.destroyed) this.draw();
        }));

        // the face crosshair owns the position; follow its edits.
        this.trackSubscription(this.item.y.subscribe(() => { if (!this.destroyed) this.draw(); }));

        this.draw();
    }

    getId(): string {
        return this.item.id;
    }

    draw() {
        if (this.destroyed) return;

        const g = this.glyph;
        g.clear();

        const edge = this.edge();
        const cy = this.item.y.get();
        const invalid = doorPlacementViolations(this.elev, this.item, this.product, this.elev.draw.formatDimension).length > 0;
        const {color, alpha} = glyphStyle(this.selectedFlag, invalid);

        const primary = this.item.kind === "handle" || this.item.kind === "lock";
        const categoryGlyph = this.categoryGlyph && this.categoryGlyph.targets.includes(this.item.target) ? this.categoryGlyph : null;

        // a category with an edge drawing wins over the primary faceplate: a rim exit device
        // seeded as the primary handle stands off the push face at the 161 height rather
        // than reading as a mortised latch.
        if (categoryGlyph?.drawEdge) {
            // an item the rules put on both faces stands off the front.
            const mount = itemFace(this.product);
            const front = this.frontFace();
            const onBack = mount !== null && front !== null && mount !== front;
            // a head placement has no height on the leaf: its face point is the leaf top.
            g.hitArea = categoryGlyph.drawEdge(g, {
                item: this.product,
                target: this.item.target,
                face: {x: onBack ? edge.x1 : edge.x0, y: this.item.target === "door" ? cy : 0},
                out: onBack ? 1 : -1,
                edge: {x0: edge.x0, x1: edge.x1},
                head: edge.head,
                selected: this.selectedFlag,
                invalid,
            });
        } else if (primary) {
            g.hitArea = this.drawFaceplate(g, edge, cy, color, alpha);
        } else if (ruleForItem(this.product)?.doorEdgeCutout) {
            // the mortise cut into the edge, centred on the door thickness.
            const size = ruleForItem(this.product)!.doorEdgeCutout!;
            const cx = (edge.x0 + edge.x1) / 2;
            const px = cx - size.width / 2;
            const py = cy - size.height / 2;
            g.lineStyle(OUTLINE, color, alpha);
            g.beginFill(0xffffff, 1);
            g.drawRoundedRect(px, py, size.width, size.height, FACEPLATE_RADIUS);
            g.endFill();
            g.hitArea = new Rectangle(px - HIT_PAD, py - HIT_PAD, size.width + 2 * HIT_PAD, size.height + 2 * HIT_PAD);
        } else {
            g.lineStyle(TICK_STROKE, color, alpha);
            g.moveTo(edge.x0, cy); g.lineTo(edge.x1, cy);
            const cx = (edge.x0 + edge.x1) / 2;
            g.moveTo(cx, cy - TICK_RISE); g.lineTo(cx, cy + TICK_RISE);
            g.hitArea = new Rectangle(edge.x0 - HIT_PAD, cy - TICK_RISE - HIT_PAD, (edge.x1 - edge.x0) + 2 * HIT_PAD, 2 * (TICK_RISE + HIT_PAD));
        }
    }

    private drawFaceplate(g: Graphics, edge: EdgeRect, cy: number, color: number, alpha: number): Rectangle {
        const cx = (edge.x0 + edge.x1) / 2;
        const px = cx - FACEPLATE_WIDTH / 2;
        const py = cy - FACEPLATE_HEIGHT / 2;

        g.lineStyle(OUTLINE, color, alpha);
        g.beginFill(color, alpha * 0.2);
        g.drawRoundedRect(px, py, FACEPLATE_WIDTH, FACEPLATE_HEIGHT, FACEPLATE_RADIUS);
        g.endFill();

        const boltHeight = this.item.kind === "lock" ? DEADBOLT_HEIGHT : LATCH_BOLT_HEIGHT;
        g.lineStyle(0);
        g.beginFill(color, alpha);
        g.drawRect(cx - BOLT_WIDTH / 2, cy - boltHeight / 2, BOLT_WIDTH, boltHeight);
        g.endFill();

        return new Rectangle(px - HIT_PAD, py - HIT_PAD, FACEPLATE_WIDTH + 2 * HIT_PAD, FACEPLATE_HEIGHT + 2 * HIT_PAD);
    }
}
